import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Collapse,
  Badge,
} from '@mui/material'
import { FilterList as FilterIcon, ExpandLess, ExpandMore, Clear } from '@mui/icons-material'
import { useState } from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { de } from 'date-fns/locale/de'
import { InvoiceFilter, InvoiceRow } from '@/types'
import { getSupplierNames, getValueRanges } from '@/utils/filterUtils'

interface FilterPanelProps {
  invoices: InvoiceRow[]
  filter: InvoiceFilter
  onFilterChange: (filter: InvoiceFilter) => void
  filteredCount: number
  totalCount: number
}

export default function FilterPanel({
  invoices,
  filter,
  onFilterChange,
  filteredCount,
  totalCount,
}: FilterPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const supplierNames = getSupplierNames(invoices)
  const ranges = getValueRanges(invoices)

  const activeFilterCount =
    (filter.supplierNames.length > 0 ? 1 : 0) +
    (filter.dateRange.start || filter.dateRange.end ? 1 : 0) +
    (filter.bruttoRange.min !== null || filter.bruttoRange.max !== null ? 1 : 0) +
    (filter.nettoRange.min !== null || filter.nettoRange.max !== null ? 1 : 0) +
    (filter.rechnungsnummerSearch ? 1 : 0) +
    (filter.commentSearch ? 1 : 0)

  const updateFilter = (updates: Partial<InvoiceFilter>) => {
    onFilterChange({ ...filter, ...updates })
  }

  const clearFilters = () => {
    onFilterChange({
      supplierNames: [],
      dateRange: { start: null, end: null },
      bruttoRange: { min: null, max: null },
      nettoRange: { min: null, max: null },
      rechnungsnummerSearch: '',
      commentSearch: '',
    })
  }

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Badge badgeContent={activeFilterCount} color="primary">
          <FilterIcon />
        </Badge>
        <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
          Filters
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
          {filteredCount} of {totalCount} invoices
        </Typography>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>

      <Collapse in={expanded}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <Stack spacing={3}>
              {/* Supplier Names */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Suppliers
                </Typography>
                <TextField
                  fullWidth
                  select
                  size="small"
                  value=""
                  onChange={(e) => {
                    const value = e.target.value
                    if (value && !filter.supplierNames.includes(value)) {
                      updateFilter({ supplierNames: [...filter.supplierNames, value] })
                    }
                  }}
                  SelectProps={{ native: true }}
                  placeholder="Add supplier filter..."
                >
                  <option value="">Add supplier...</option>
                  {supplierNames
                    .filter((name) => !filter.supplierNames.includes(name))
                    .map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                </TextField>

                {filter.supplierNames.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {filter.supplierNames.map((name) => (
                      <Button
                        key={name}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          updateFilter({
                            supplierNames: filter.supplierNames.filter((n) => n !== name),
                          })
                        }}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {name}
                        <Clear sx={{ ml: 0.5, fontSize: '0.875rem' }} />
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Date Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Invoice Date Range
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <DatePicker
                    label="From"
                    value={filter.dateRange.start}
                    onChange={(date) => updateFilter({ dateRange: { ...filter.dateRange, start: date } })}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                    maxDate={filter.dateRange.end || undefined}
                  />
                  <DatePicker
                    label="To"
                    value={filter.dateRange.end}
                    onChange={(date) => updateFilter({ dateRange: { ...filter.dateRange, end: date } })}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                    minDate={filter.dateRange.start || undefined}
                  />
                </Stack>
              </Box>

              {/* BRUTTO Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  BRUTTO Range
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <TextField
                    label="Min"
                    type="number"
                    size="small"
                    value={filter.bruttoRange.min ?? ''}
                    onChange={(e) =>
                      updateFilter({
                        bruttoRange: {
                          ...filter.bruttoRange,
                          min: e.target.value ? parseFloat(e.target.value) : null,
                        },
                      })
                    }
                    sx={{ minWidth: 120 }}
                    placeholder="0"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                  <TextField
                    label="Max"
                    type="number"
                    size="small"
                    value={filter.bruttoRange.max ?? ''}
                    onChange={(e) =>
                      updateFilter({
                        bruttoRange: {
                          ...filter.bruttoRange,
                          max: e.target.value ? parseFloat(e.target.value) : null,
                        },
                      })
                    }
                    sx={{ minWidth: 120 }}
                    placeholder={String(ranges.brutto.max)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Stack>
              </Box>

              {/* NETTO Range */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  NETTO Range
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <TextField
                    label="Min"
                    type="number"
                    size="small"
                    value={filter.nettoRange.min ?? ''}
                    onChange={(e) =>
                      updateFilter({
                        nettoRange: {
                          ...filter.nettoRange,
                          min: e.target.value ? parseFloat(e.target.value) : null,
                        },
                      })
                    }
                    sx={{ minWidth: 120 }}
                    placeholder="0"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                  <TextField
                    label="Max"
                    type="number"
                    size="small"
                    value={filter.nettoRange.max ?? ''}
                    onChange={(e) =>
                      updateFilter({
                        nettoRange: {
                          ...filter.nettoRange,
                          max: e.target.value ? parseFloat(e.target.value) : null,
                        },
                      })
                    }
                    sx={{ minWidth: 120 }}
                    placeholder={String(ranges.netto.max)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Stack>
              </Box>

              {/* Search Fields */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Search
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Invoice Number"
                  value={filter.rechnungsnummerSearch}
                  onChange={(e) => updateFilter({ rechnungsnummerSearch: e.target.value })}
                  placeholder="Contains..."
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Comment"
                  value={filter.commentSearch}
                  onChange={(e) => updateFilter({ commentSearch: e.target.value })}
                  placeholder="Contains..."
                />
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  disabled={activeFilterCount === 0}
                >
                  Clear All Filters
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </CardContent>
      </Collapse>
    </Card>
  )
}
